/**
 * API Response Types
 * Type definitions for backend API responses
 */

import type { EligibilityClass, JucoYear } from '../utils/roster-class';

export type ProspectResponse = {
  id: string;
  firstName: string;
  lastName: string;
  position: string | null;
  gradYear: number | null;
  /**
   * Year of eligibility ("1".."5") when the roster said so. Optional because an API deployed
   * before this field existed simply omits it - the web and API ship as two workflows, so a
   * new client can be talking to an older server.
   */
  eligibilityClass?: EligibilityClass | null;
  /**
   * Year of junior college ("1".."3"), the second of the two ladders a roster's Class column
   * answers - a redshirt year counts here where it does not count for `eligibilityClass`.
   * Optional for the same deploy-window reason as the field above.
   */
  jucoYear?: JucoYear | null;
  /**
   * Level ordinal: 0 High School, 1 JUCO, 2 Transfer Portal. `0` is falsy - see
   * `constants/playerLevels`, and never test this field for truthiness.
   *
   * Optional both because an older server omits it and because `about.playerLevel` remains
   * populated for one release; read this first and fall back to the `about` copy.
   */
  playerLevel?: number | null;
  /**
   * The player's school or organization, as a roster import recorded it. Optional because an
   * API deployed before this field existed omits it.
   */
  school?: string | null;
  teams: TeamInfo[];
  email: string | null;
  phone: string | null;
  height: string | null;
  weight: number | null;
  bats: string | null;
  throws: string | null;
  isFavorite: boolean;
  favoritedAt: string | null;
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
  deletedAt: string | null;
  createdBy: string | null;
  tagValues: ProspectTagValueResponse[];
  defaultTags?: ProspectDefaultTag[];
  /** Count of non-deleted RecruitingNote rows on this prospect, scoped to the requesting org. */
  noteCount?: number;
  /** Count of non-deleted ProspectVideo rows on this prospect, scoped to the requesting org. */
  videoCount?: number;
  about: ProspectAboutResponse | null;
  positionPlayerMetrics: ProspectPositionPlayerMetricsResponse | null;
  pitcherMetrics: ProspectPitcherMetricsResponse | null;
  /** Recent metric history entries (up to 3 per metric, most recent first) */
  metricEntries?: MetricEntryResponse[];
};

export type TeamInfo = {
  id: string;
  name: string;
  jerseyNumber: number | null;
};

export type UpdateProspectTeamRequest = {
  jerseyNumber: number | null;
};

export type RecruitingNoteType = 'text' | 'handwritten';

/**
 * A recruiting note that may be attached to a prospect. Created via the mobile
 * app — on web we currently read-only surface them via the Latest Notes feed.
 */
export type RecruitingNoteResponse = {
  id: string;
  organizationId: string;
  prospectId: string | null;
  prospectName: string | null;
  /**
   * Team this note annotates, when it is a team-scoped roster annotation rather
   * than a prospect note. Exactly one of `prospectId` / `teamId` is non-null
   * (enforced server-side by a CHECK constraint). Team notes have a null
   * `prospectId`/`prospectName` — consumers must tolerate that.
   */
  teamId?: string | null;
  /**
   * For team roster annotations: which roster-image snapshot (UploadMetadata id)
   * the strokes were drawn on. Advisory staleness token — compare against the
   * team's current roster upload to detect "a newer roster exists". `null` for
   * prospect notes and pre-team-notes API responses.
   */
  rosterUploadId?: string | null;
  content: string;
  title: string | null;
  noteType: RecruitingNoteType;
  imageUrl: string | null;
  drawingDataUrl: string | null;
  createdAt: string;
  updatedAt: string | null;
  /**
   * Author, or `null` for an org-owned note that nobody authored — currently only
   * roster imports. `null` also means any member of the organization may edit it,
   * rather than only the author.
   */
  createdBy: string | null;
  createdByName: string | null;
  updatedBy: string | null;
  historyCount: number;
  /**
   * Client-supplied idempotency key for mobile-authored notes. May be
   * `undefined` on responses returned by API versions prior to the Phase 3
   * rollout of the iPad notes hardening plan.
   */
  clientGeneratedId?: string | null;
  /**
   * Monotonically-increasing version of the note, bumped on each server-side
   * write. May be `undefined` pre-Phase-3 deploy.
   */
  version?: number;
  /**
   * ISO-8601 UTC timestamp when the signed `imageUrl` stops working. Consumers
   * should pre-emptively refetch the note before this time. May be `undefined`
   * pre-Phase-3 deploy — in that case, fall back to `<img onError>`-based
   * recovery.
   */
  imageUrlExpiresAt?: string | null;
  /**
   * ISO-8601 UTC timestamp when the signed `drawingDataUrl` stops working.
   * Same handling as `imageUrlExpiresAt`.
   */
  drawingDataUrlExpiresAt?: string | null;
};

export type ProspectTagValueResponse = {
  id: string;
  prospectId: string;
  tagKey: string;
  tagValue: string;
  createdAt: string;
  updatedAt: string;
};

export type ProspectDefaultTag = {
  id: string;
  prospectId: string;
  tagName: string;
  tagValue: string;
  createdAt: string;
  updatedAt: string;
};

export type ProspectAboutResponse = {
  twitterUrl?: string | null;
  instagramUrl?: string | null;
  playerLevel?: number | null;
  city?: string | null;
  state?: string | null;
  birthDate?: string | null;
  visitDate?: string | null;
  address?: string | null;
  zipCode?: string | null;
  parentName?: string | null;
  parentPhone?: string | null;
  coachName?: string | null;
  coachPhone?: string | null;
  advisorName?: string | null;
  advisorPhone?: string | null;
};

export type ProspectPositionPlayerMetricsResponse = {
  // Base metrics (velocities, times, strength)
  homeToFirst: number | null;
  popTime: number | null;
  exitVelocity: number | null;
  infieldVelocity: number | null;
  outfieldVelocity: number | null;
  sixtyTime: number | null;
  catcherVelocity: number | null;
  batSpeed: number | null;
  leftGripStrength: number | null;
  rightGripStrength: number | null;

  // 80/20 Scale ratings (Present + Future for each)
  hitPresent: number | null;
  hitFuture: number | null;
  powerPresent: number | null;
  powerFuture: number | null;
  fieldPresent: number | null;
  fieldFuture: number | null;
  throwPresent: number | null;
  throwFuture: number | null;
  runPresent: number | null;
  runFuture: number | null;
};

export type ProspectPitcherMetricsResponse = {
  // Baseball Pitches (8 types)
  fastball4SeamVelocity: number | null;
  fastball4SeamPresent: number | null;
  fastball4SeamFuture: number | null;

  fastball2SeamVelocity: number | null;
  fastball2SeamPresent: number | null;
  fastball2SeamFuture: number | null;

  sliderVelocity: number | null;
  sliderPresent: number | null;
  sliderFuture: number | null;

  curveballVelocity: number | null;
  curveballPresent: number | null;
  curveballFuture: number | null;

  changeupVelocity: number | null;
  changeupPresent: number | null;
  changeupFuture: number | null;

  knuckleballVelocity: number | null;
  knuckleballPresent: number | null;
  knuckleballFuture: number | null;

  splitterVelocity: number | null;
  splitterPresent: number | null;
  splitterFuture: number | null;

  cutterVelocity: number | null;
  cutterPresent: number | null;
  cutterFuture: number | null;

  // Softball Pitches (7 types)
  //
  // The curveball's fields are named `softballCurve*` rather than `softballCurveball*`. That is
  // deliberate and matches the API/DB columns — renaming them would orphan every already-recorded
  // curveball reading. Only the user-visible label says "Curveball".
  softballFastballVelocity: number | null;
  softballFastballPresent: number | null;
  softballFastballFuture: number | null;

  softballChangeupVelocity: number | null;
  softballChangeupPresent: number | null;
  softballChangeupFuture: number | null;

  softballScrewballVelocity: number | null;
  softballScrewballPresent: number | null;
  softballScrewballFuture: number | null;

  softballDropballVelocity: number | null;
  softballDropballPresent: number | null;
  softballDropballFuture: number | null;

  softballRiseballVelocity: number | null;
  softballRiseballPresent: number | null;
  softballRiseballFuture: number | null;

  softballCurveVelocity: number | null;
  softballCurvePresent: number | null;
  softballCurveFuture: number | null;

  softballOffSpeedVelocity: number | null;
  softballOffSpeedPresent: number | null;
  softballOffSpeedFuture: number | null;

  // Spin rate (rpm) — softball only. Decimal server-side, whole numbers in practice.
  softballFastballSpinRate: number | null;
  softballChangeupSpinRate: number | null;
  softballScrewballSpinRate: number | null;
  softballDropballSpinRate: number | null;
  softballRiseballSpinRate: number | null;
  softballCurveSpinRate: number | null;
  softballOffSpeedSpinRate: number | null;
};

export type TeamResponse = {
  id: string;
  name: string;
  headCoachId?: string;
  headCoachName?: string;
  headCoachPreferredContactMethod?: string;
  createdAt: string;
  updatedAt: string;
};

export type UpdateTeamRequest = {
  name: string;
};

export type TeamCoachResponse = {
  id: string;
  teamId: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  twitterHandle: string | null;
  createdAt: string;
  updatedAt: string;
};

export type TeamCoachPayload = {
  firstName: string;
  lastName: string;
  email?: string | null;
  phone?: string | null;
  twitterHandle?: string | null;
};

export type AddProspectToTeamResponse = {
  prospectId: string;
  teamId: string;
  prospectName: string;
  teamName: string;
};

export type BulkAddProspectsToTeamRequest = {
  prospectIds: string[];
};

export type BulkAddProspectsToTeamResponse = {
  teamId: string;
  teamName: string;
  addedCount: number;
  alreadyOnTeamProspectIds: string[];
  notFoundProspectIds: string[];
};

export type CreateTeamRequest = {
  name: string;
};

export type CreateTeamResponse = {
  id: string;
  name: string;
  isNew: boolean;
};

/** A team in the org that currently has zero players (candidate for cleanup). */
/** A live team in the org, with the counts shown before a bulk delete is confirmed. */
export type ManageableTeamResponse = {
  id: string;
  name: string;
  createdAt: string;
  /** Live players on this team's roster. Zero marks a cleanup candidate. */
  playerCount: number;
  /** Schedule games linked to this team; the links survive a delete and come back on restore. */
  gameLinkCount: number;
};

/** A soft-deleted team, restorable from the manage-teams panel. */
export type DeletedTeamResponse = {
  id: string;
  name: string;
  createdAt: string;
  /** Null for teams tombstoned before the timestamp was recorded — those restore empty. */
  deletedAt: string | null;
  /** Players a restore would put back on this team's roster. */
  restorablePlayerCount: number;
};

export type BulkTeamIdsRequest = {
  teamIds: string[];
};

/** Body for requesting a signed URL to upload a file (e.g. schedule) to a team. */
export type TeamUploadUrlRequest = {
  fileName: string;
  fileExtension: string;
  contentType: string;
  fileSizeBytes?: number;
};

/** Response containing the signed URL and identifiers to complete a team upload. */
export type TeamUploadUrlResponse = {
  uploadId: string;
  uploadUrl: string;
  gcsObjectName: string;
};

/** A file uploaded to a team (e.g. a schedule). Includes a fresh signed download URL. */
export type TeamUploadResponse = {
  id: string;
  teamId: string;
  fileName: string;
  contentType: string;
  fileSizeBytes: number | null;
  downloadUrl: string;
  uploadedBy: string;
  uploadedByName: string | null;
  createdAt: string;
  urlExpiresAt: string;
};

/** Outcome of a bulk team delete or restore. */
export type BulkTeamActionResponse = {
  affectedCount: number;
  affectedIds: string[];
  /** Ids the server did not act on — out of org, gone, or already in the target state. */
  skippedIds: string[];
};

export type PaginatedResponse<T> = {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  hasMore: boolean;
};

export type CreateProspectRequest = {
  firstName: string;
  lastName: string;
  jerseyNumber: number;
  teams: string[];
  gradYear?: number;
  /** Year of eligibility ("1".."5"). Omitted leaves it unchanged. */
  eligibilityClass?: EligibilityClass;
  /** Year of junior college ("1".."3"). Only meaningful when `playerLevel` is JUCO. */
  jucoYear?: JucoYear;
  /** Level ordinal: 0 High School, 1 JUCO, 2 Transfer Portal. `0` is a value, not an absence. */
  playerLevel?: number;
  position?: string;
};

export type UpdateProspectRequest = {
  firstName?: string;
  lastName?: string;
  jerseyNumber?: number;
  teams?: string[];
  gradYear?: number;
  /** Year of eligibility ("1".."5"). Omitted leaves it unchanged. */
  eligibilityClass?: EligibilityClass;
  /** Year of junior college ("1".."3"). Only meaningful when `playerLevel` is JUCO. */
  jucoYear?: JucoYear;
  /**
   * Level ordinal: 0 High School, 1 JUCO, 2 Transfer Portal. `0` is a value, not an absence.
   *
   * `about.playerLevel` below is the pre-move home of the same value and is still accepted for
   * one release; a client that sets one should set both.
   */
  playerLevel?: number;
  position?: string;
  email?: string;
  phone?: string;
  height?: string;
  weight?: number;
  bats?: string;
  throws?: string;
  isFavorite?: boolean;
  about?: {
    twitterUrl?: string;
    instagramUrl?: string;
    playerLevel?: number;
    city?: string;
    state?: string;
    birthDate?: string;
    visitDate?: string;
    address?: string;
    zipCode?: string;
    parentName?: string;
    parentPhone?: string;
    coachName?: string;
    coachPhone?: string;
    advisorName?: string;
    advisorPhone?: string;
  };
};

export type TeamSearchResult = {
  id: string;
  name: string;
  /**
   * True when the team has a canonical roster image. Lets the typeahead surface
   * a "has roster" indicator without resolving a (short-lived) signed URL up
   * front. Optional for back-compat with pre-roster-image API responses.
   */
  hasRosterImage?: boolean;
};

/**
 * Resolution payload for GET /api/teams/{id}/roster-image. The signed URL is
 * freshly minted on every call and short-lived — never cache it past
 * `expiresAt`. The endpoint 404s when the team has no roster image or its GCS
 * object is missing.
 */
export type TeamRosterImageResponse = {
  teamId: string;
  rosterUploadId: string;
  signedUrl: string;
  /** ISO-8601 UTC timestamp after which `signedUrl` stops working. */
  expiresAt: string;
};

export type CreateTeamWithPlayersRequest = {
  team?: string;
  players?: ShowcaseProspectRequest[];
  /**
   * Optional originating UploadMetadata id. When supplied, an import that
   * creates/updates players advances the team's canonical roster image to this
   * upload ("most recent roster wins"). The web roster upload wizard surfaces
   * this id from the upload step and sends it for image uploads (it is omitted
   * for CSV/Excel/PDF, which have no viewable roster image).
   */
  rosterUploadId?: string;
  /**
   * Optional id of the roster extraction (`roster_extractions.id`, the
   * `extractionId` the extraction endpoints return) the players were reviewed
   * from. Recorded as each imported prospect's provenance, which a system admin
   * can see. A different value from `rosterUploadId`. Omitted when unknown; the
   * server ignores an id that is not the caller's organization's.
   */
  extractionId?: string;
  /**
   * How the players were entered, recorded as each prospect's provenance. `"extraction"` for a
   * roster import (the server's default when absent); `"manual"` when a coach typed the players in,
   * in which case the server records a manual entry and ignores `extractionId`. A roster reviewed
   * and edited after extraction is still `"extraction"`.
   */
  entryMethod?: CreateTeamWithPlayersEntryMethod;
};

export type CreateTeamWithPlayersEntryMethod = 'extraction' | 'manual';

export type CreateTeamWithPlayersResponse = {
  teamId: string;
  teamName?: string;
  totalProspectsCreated: number;
  totalProspectsLinked: number;
  skippedDuplicates: number;
  teamAlreadyExisted: boolean;
};

/**
 * Partial metric update types
 * Used for PATCH operations where only specific fields are updated
 */

/**
 * Partial update type for position player metrics
 * All fields are optional and can be set to null to clear values
 */
export type PartialPositionPlayerMetrics = {
  [K in keyof ProspectPositionPlayerMetricsResponse]?: ProspectPositionPlayerMetricsResponse[K] | null;
};

/**
 * Partial update type for pitcher metrics
 * All fields are optional and can be set to null to clear values
 */
export type PartialPitcherMetrics = {
  [K in keyof ProspectPitcherMetricsResponse]?: ProspectPitcherMetricsResponse[K] | null;
};

/**
 * Showcase-related types
 */

export interface ShowcaseProspectRequest {
  firstName: string;
  lastName: string;
  jerseyNumber?: number;
  position?: string;
  gradYear?: number;
  /** Year of eligibility ("1".."5") when the roster's Class column named one. */
  eligibilityClass?: EligibilityClass;
  /**
   * Year of junior college ("1".."3"), set by the roster import's per-team JUCO toggle. Only
   * meaningful alongside `playerLevel` being JUCO, and named in `overwriteFields` so it wins over
   * a stale value from an earlier import.
   */
  jucoYear?: JucoYear;
  /**
   * Level ordinal: 0 High School, 1 JUCO, 2 Transfer Portal. `0` IS A VALUE — never test this
   * field for truthiness, and never `playerLevel || undefined` it out of a payload.
   */
  playerLevel?: number;
  email?: string;
  phone?: string;
  height?: string;
  weight?: number;
  bats?: string;
  throws?: string;
  /** The player's school, from the roster's School / School Name / University Name column. */
  school?: string;
  twitter?: string;
  instagram?: string;
  city?: string;
  state?: string;
  address?: string;
  zipCode?: string;
  birthDate?: string;
  parentName?: string;
  parentPhone?: string;
  coachName?: string;
  coachPhone?: string;
  advisorName?: string;
  advisorPhone?: string;
  notes?: string;
  /**
   * Set by the duplicate-import guard when the coach resolved this row to a player who already
   * exists: add that player to the team and update them in place instead of creating a second
   * record.
   */
  linkToProspectId?: string;
  /**
   * Field names (camelCase) where this row's value should win over the stored one. Read alongside
   * `linkToProspectId` for the fields a coach picked in the duplicate guard, and on its own for
   * the two the JUCO toggle owns — the API lets those overwrite however the row was matched.
   * Anything not listed is left as-is on the existing record, blanks aside.
   */
  overwriteFields?: string[];
}

export interface ShowcaseTeamRequest {
  teamColor?: string;
  teamNumber?: number;
  players?: ShowcaseProspectRequest[];
}

export interface CreateShowcaseRequest {
  showcaseName?: string;
  location?: string;
  teams?: ShowcaseTeamRequest[];
}

export interface AddTeamsToShowcaseRequest {
  teams?: ShowcaseTeamRequest[];
}

export interface ShowcaseTeamResponse {
  teamId: string;
  teamName?: string;
  prospectCount: number;
}

export interface ShowcaseResponse {
  id: string;
  name?: string;
  location?: string;
  createdAt: string;
  createdBy?: string;
  teams?: ShowcaseTeamResponse[];
}

export interface BulkCreateShowcaseResponse {
  showcase: ShowcaseResponse;
  totalProspectsCreated: number;
  totalProspectsLinked: number;
  totalTeamsCreated: number;
}

export interface AddTeamsToShowcaseResponse {
  showcaseId: string;
  teamsAdded: number;
  teamsUpdated: number;
  totalProspectsAdded: number;
  message?: string;
}

export interface ShowcaseSearchResult {
  id: string;
  name?: string;
}

/**
 * Metric Entry types for history tracking
 */
export type MetricEntryResponse = {
  id: string;
  metricName: string;
  metricCategory: 'position_player' | 'pitcher';
  value: number;
  recordedByUserId: string;
  recordedByName: string;
  recordedAt: string;
};

export type GetMetricEntriesParams = {
  metricName?: string;
  metricCategory?: 'position_player' | 'pitcher';
  pageSize?: number;
  pageNumber?: number;
};

/**
 * Prospect-merge types — mirror the backend DTOs in
 * `api/RosterPilotAPI/DTOs/MergeProspects*.cs` and `MergeErrorResponse.cs`.
 */

/**
 * The survivor's final resolved scalar values. Mirrors the C# `PatchProspectRequest` shape.
 *
 * A field carrying a VALUE is applied; an absent field is untouched; and a field sent as `null`
 * is **ignored**, not cleared — which is the whole reason `nullFields` exists. Sending
 * `{ phone: null }` and expecting the phone to be cleared is a real bug that has already been
 * written twice against this contract, so the asymmetry is stated rather than implied:
 * `nullFields` is the only thing that clears a scalar.
 */
export type ResolvedFields = {
  firstName?: string | null;
  lastName?: string | null;
  position?: string | null;
  gradYear?: number | null;
  /** Year of eligibility ("1".."5"). */
  eligibilityClass?: EligibilityClass | null;
  /** Year of junior college ("1".."3"). Only meaningful alongside a JUCO `playerLevel`. */
  jucoYear?: JucoYear | null;
  /**
   * Level ordinal: 0 High School, 1 JUCO, 2 Transfer Portal. `0` is falsy - test `!= null`,
   * and never `playerLevel || undefined` it out of a merge payload.
   */
  playerLevel?: number | null;
  email?: string | null;
  phone?: string | null;
  height?: string | null;
  weight?: number | null;
  bats?: string | null;
  throws?: string | null;
  /** camelCase field name → value (or null to clear). */
  about?: Record<string, unknown>;
  positionPlayerMetrics?: Record<string, unknown>;
  pitcherMetrics?: Record<string, unknown>;
  /** Top-level scalar fields to explicitly clear (set to null). */
  nullFields?: string[];
};

export type MergeProspectsRequest = {
  survivingProspectId: string;
  /** At least one distinct id; must NOT contain the survivor. */
  mergedProspectIds: string[];
  /** `updatedAt` the client last saw for each participating prospect (ISO-8601). */
  expectedVersions: Record<string, string>;
  resolvedFields: ResolvedFields;
};

/** Per-child-type counts of rows repointed onto the survivor during a merge. */
export type MergeMovedCounts = {
  notes: number;
  videos: number;
  documents: number;
  metricEntries: number;
  defaultTags: number;
  tagValues: number;
  teams: number;
  events: number;
};

export type MergeProspectsResponse = {
  survivingProspectId: string;
  mergedProspectIds: string[];
  moved: MergeMovedCounts;
  auditId: string;
};

/** Machine-readable merge error codes — mirrors C# `MergeErrorCodes`. */
export type MergeErrorCode =
  | 'SELF_MERGE'
  | 'EMPTY_LOSERS'
  | 'REQUIRED_FIELD_BLANK'
  | 'INVALID_FIELD'
  | 'FORBIDDEN_ROLE'
  | 'CROSS_ORG'
  | 'PROSPECT_NOT_FOUND'
  | 'STALE_READ'
  | 'ALREADY_MERGED'
  | 'IDEMPOTENCY_MISMATCH'
  /** Bulk merge only: the duplicate group named by the request no longer exists. */
  | 'CANDIDATE_NOT_FOUND'
  /** Bulk merge only: an unexpected per-group failure, reported without failing the run. */
  | 'MERGE_FAILED';

/** Machine-readable error body returned by the merge endpoint (400/403/404/409). */
export type MergeErrorResponse = {
  code: MergeErrorCode;
  message: string;
  conflictingIds: string[];
};

/**
 * Duplicate-import guard types — mirror the backend DTOs in
 * `api/RosterPilotAPI/DTOs/CheckImportDuplicatesRequest.cs` and
 * `CheckImportDuplicatesResponse.cs`. Used to detect roster-import candidates
 * that likely already exist (name + grad year + position + team) BEFORE any
 * records are created, so the guard can offer skip / import-as-new.
 */
/**
 * A roster row being checked against existing players. Name, grad year, position
 * and team drive the match; the rest are sent so the response can diff them
 * against the stored record and show the coach where the two disagree.
 */
export type ImportCandidate = {
  firstName: string;
  lastName: string;
  gradYear?: number | null;
  position?: string | null;
  email?: string | null;
  phone?: string | null;
  height?: string | null;
  weight?: number | null;
  bats?: string | null;
  throws?: string | null;
};

export type CheckImportDuplicatesRequest = {
  /** Target team the roster is importing into, when known (strengthens matching). */
  teamId?: string | null;
  /** Candidate players in display order; `index` in the response maps back to this list. */
  players: ImportCandidate[];
};

/**
 * An existing prospect a candidate matched. Carries the same comparable fields as
 * {@link ImportCandidate} so the guard can show a field-by-field diff before the
 * roster row is merged into this record.
 */
export type DuplicateMatch = {
  prospectId: string;
  firstName: string;
  lastName: string;
  gradYear: number | null;
  position: string | null;
  email: string | null;
  phone: string | null;
  height: string | null;
  weight: number | null;
  bats: string | null;
  throws: string | null;
  teams: string[];
  /** Always includes "name", plus any of "gradYear", "position", "team". */
  matchedOn: string[];
};

/** A candidate flagged as a likely duplicate, with the existing prospect(s) it matched. */
export type ImportDuplicateFlag = {
  /** Zero-based index into the request's `players` list. */
  index: number;
  candidate: ImportCandidate;
  matches: DuplicateMatch[];
};

export type CheckImportDuplicatesResponse = {
  /** Only flagged candidates are returned; unflagged candidates import normally. */
  flagged: ImportDuplicateFlag[];
};

// ---------------------------------------------------------------------------
// Merge Manager — org-wide duplicate detection
// ---------------------------------------------------------------------------

/**
 * Mirrors `api/RosterPilotAPI/DTOs/DuplicateCandidateResponse.cs`. Unlike the
 * import guard above, these are duplicates that are *already* in the system.
 */
export type DuplicateCandidateMember = {
  prospectId: string;
  firstName: string;
  lastName: string;
  gradYear?: number | null;
  position?: string | null;
  teams: string[];
  /** Oldest member survives a bulk merge, so the client can show which record wins. */
  createdAt: string;
};

export type DuplicateCandidate = {
  id: string;
  memberCount: number;
  /** Always includes `name`, plus any of `gradYear`, `position`, `team`. */
  matchedOn: string[];
  /** Corroborator count (matchedOn minus `name`). Drives strongest-first ordering. */
  confidence: number;
  /** True when merging would silently drop a custom tag value. */
  hasTagConflict: boolean;
  computedAt: string;
  members: DuplicateCandidateMember[];
};

export type DuplicateCountResponse = { count: number };

export type DuplicateSweepResponse = { groupCount: number };

export type DismissedDuplicate = {
  id: string;
  prospectA: DuplicateCandidateMember;
  prospectB: DuplicateCandidateMember;
  dismissedAt: string;
  dismissedByName?: string | null;
};

export type BulkMergeGroupRequest = {
  candidateId: string;
  /** Omit to let the server pick the oldest member. */
  survivingProspectId?: string;
  /** Omit to merge every other member into the survivor. */
  mergedProspectIds?: string[];
};

export type BulkMergeRequest = {
  /** Combined with each candidate id to form a per-group idempotency key. */
  runId?: string;
  groups: BulkMergeGroupRequest[];
};

export type BulkMergeGroupResult = {
  candidateId: string;
  success: boolean;
  survivingProspectId: string;
  mergedProspectIds: string[];
  errorCode?: MergeErrorCode | null;
  errorMessage?: string | null;
};

/** A partial run is a normal outcome — the endpoint always returns 200. */
export type BulkMergeResponse = {
  results: BulkMergeGroupResult[];
  succeededCount: number;
  failedCount: number;
};

export type MergeAllStatus =
  | "queued"
  | "running"
  | "completed"
  | "failed"
  | "cancelled";

/**
 * An org-wide "merge every duplicate group" run. The client sends no ids — an
 * organization can carry thousands of groups, so the server walks them itself
 * and this reports where it got to.
 */
export type MergeAllJob = {
  jobId: string;
  status: MergeAllStatus;
  /**
   * True when a running job has stopped reporting progress — its process is gone,
   * usually because a deploy landed mid-run. The server reclaims it, but the page
   * says so rather than animating a count that is not moving.
   */
  stalled: boolean;
  /** Open groups when the run was accepted — what progress is scaled against. */
  totalGroups: number;
  processedGroups: number;
  succeededCount: number;
  /** Groups that could not be merged. They stay on the list. */
  failedCount: number;
  errorMessage?: string | null;
  createdAt: string;
  startedAt?: string | null;
  completedAt?: string | null;
};

// ---------------------------------------------------------------------------
// Calendar + Tournament Events
// ---------------------------------------------------------------------------

/**
 * Kind of calendar event. A code enum mirrored from the C# `CalendarEventType`
 * enum; the set is stable and adding a value is a deploy on both ends.
 */
export type CalendarEventType = 'Tournament' | 'RecruitVisit' | 'OneOff';

/** A coach assigned to an event (org-local; drives the "who's going" avatars). */
export type CalendarEventAssignee = {
  userId: string;
  displayName: string;
  initials: string;
};

/** Slim org member for the assignee picker (from GET /api/users/organization-members). */
export type OrganizationMember = {
  id: string;
  displayName: string;
  initials: string;
  role: string;
};

/**
 * A single org-scoped calendar event.
 *
 * IMPORTANT timezone contract (see plan Data Design Decision #2):
 * - **Timed events** (`isAllDay === false`): `startsAtUtc` / `endsAtUtc` are
 *   true UTC instants. Render them in the event's own `timeZone` (IANA id) via
 *   `Intl.DateTimeFormat(locale, { timeZone, timeZoneName: 'short' })`. The
 *   calendar buckets a timed event onto a day using the event's timezone, never
 *   the viewer's.
 * - **All-day / multi-day events** (`isAllDay === true`): stored as
 *   date-at-midnight UTC and always formatted with `timeZone: 'UTC'`, date only.
 *   `endsAtUtc` is stored **exclusive** — the midnight-UTC of the day *after* the
 *   last day (a Jul 5→Jul 7 tournament stores end = Jul 8 00:00Z). Subtract one
 *   day to get the inclusive last day for display.
 *
 * Fields beyond Slice 1's scope (location*, itineraryUrl, distribution*) are
 * present on the DTO because the API returns them, but their editing UI lands
 * in later slices.
 */
export type CalendarEventResponse = {
  id: string;
  title: string;
  eventType: CalendarEventType;
  /** True UTC instant (timed) or date-at-midnight UTC (all-day). ISO-8601. */
  startsAtUtc: string;
  /** Exclusive end for all-day events; true UTC instant for timed events. ISO-8601. */
  endsAtUtc: string;
  isAllDay: boolean;
  /** IANA timezone id the timed event's times should be rendered in. */
  timeZone: string;
  /** True when the timezone was assumed (Places failure) rather than resolved. */
  timezoneAssumed: boolean;
  locationName: string | null;
  locationAddress: string | null;
  locationLat: number | null;
  locationLng: number | null;
  locationPlaceId: string | null;
  itineraryUrl: string | null;
  notes: string | null;
  distributionBatchId: string | null;
  isDistributedCopy: boolean;
  /**
   * True on a distributed copy the recipient has not yet set up (matched teams,
   * followed players, reviewed, assigned) via the Build Itinerary wizard. Cleared
   * by POST /api/tournaments/{id}/complete-setup.
   */
  needsSetup: boolean;

  /**
   * True when staff have published this tournament to the shared itinerary catalog, so any
   * program can find it by date and add it. Set only through the system_admin publish
   * endpoint (`setCatalogPublication`), never through an ordinary event update.
   */
  isShareable: boolean;
  isLocallyEdited: boolean;
  /**
   * The repeating series this event belongs to, or null for a one-off. Occurrences are ordinary
   * materialized events, so "part of a series" is exactly this being non-null — which is what
   * gates the This / This and following / All picker on edit and delete.
   */
  recurrenceId: string | null;
  /** 0-based position in the series (week one is 0). Null for a one-off. */
  recurrenceIndex: number | null;
  /**
   * True once a coach has edited this occurrence on its own ("move only week three"). Later
   * ThisAndFollowing / All writes skip it so that deliberate change is never undone.
   */
  recurrenceDetached: boolean;
  /**
   * The weekdays the series runs on (`Date.getDay()` numbers, ascending). Null for a one-off and
   * for a series with no weekdays picked. Optional because an API older than weekday selection
   * never sends it.
   */
  daysOfWeek?: number[] | null;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** Coaches responsible for this event. Everyone in the org still sees the event. */
  assignees: CalendarEventAssignee[];
  /**
   * The caller's OWN games within this tournament — populated only in the
   * "My events" (scope=mine) read, and only for tournaments the caller covers at
   * the game level. Empty otherwise. "My events" expands these into individual
   * dated items instead of showing the whole-tournament bar.
   */
  myGames?: MyTournamentGame[];
  /**
   * Coaches on at least one of this tournament's games who are NOT already in `assignees`.
   * Optional because the web can deploy before the API that sends it: read it through
   * `eventGameCoaches()` so a missing field is treated as none.
   */
  gameCoaches?: CalendarEventGameCoach[];
};

/** A coach covering some of a tournament's games without being an event-level assignee. */
export type CalendarEventGameCoach = {
  userId: string;
  displayName: string;
  initials: string;
  /** How many of the tournament's games this coach is on. */
  gameCount: number;
};

/** A game the caller is personally assigned to, for the "My events" expansion. */
export type MyTournamentGame = {
  gameId: string;
  startsAtUtc: string;
  timeZone: string;
  timezoneAssumed: boolean;
  fieldName: string;
  homeTeamNameRaw: string;
  awayTeamNameRaw: string;
};

/**
 * Body for creating a calendar event. `timeZone` is optional — when omitted the
 * API falls back to the organization's default timezone and flags the event
 * `timezoneAssumed`. Location / itinerary fields are optional and unused by the
 * Slice 1 editor.
 */
export type CreateCalendarEventRequest = {
  title: string;
  eventType: CalendarEventType;
  startsAtUtc: string;
  endsAtUtc: string;
  isAllDay: boolean;
  timeZone?: string;
  locationName?: string | null;
  locationAddress?: string | null;
  locationLat?: number | null;
  locationLng?: number | null;
  locationPlaceId?: string | null;
  itineraryUrl?: string | null;
  notes?: string | null;
  /** Coach user ids to assign. Ids outside the caller's org are ignored server-side. */
  assigneeUserIds?: string[];
  /**
   * Optional repeat rule. The server MATERIALIZES `count` events (the posted one plus its later
   * occurrences) and responds with the FIRST of them, so the caller re-reads the calendar range to
   * pick up the rest. Tournaments cannot repeat and are rejected with a 400.
   */
  recurrence?: RecurrenceRule;
};

/** How often a repeating event repeats. */
export type RecurrenceFrequency = "Daily" | "Weekly" | "Monthly";

/** The largest number of occurrences the server will materialize for one series. */
export const MAX_RECURRENCE_COUNT = 260;

/**
 * How far ahead the server materializes a series with no end date
 * (`EventRecurrence.DefaultHorizonMonths`). Null is NOT "forever" — every occurrence is a real
 * calendar row — so the UI has to say what a blank end date actually does.
 */
export const RECURRENCE_DEFAULT_HORIZON_MONTHS = 12;

/** A repeat rule on create: "every `interval` `frequency`, until `untilDateUtc`". */
export type RecurrenceRule = {
  frequency: RecurrenceFrequency;
  /** Every N periods — 1 = every week, 2 = every other week. At least 1. */
  interval: number;
  /**
   * The last day the series should run, INCLUSIVE, at UTC midnight. Optional: a coach setting up a
   * weekly practice usually has no end date in mind, and the server then materializes
   * {@link RECURRENCE_DEFAULT_HORIZON_MONTHS} months of it (capped at
   * {@link MAX_RECURRENCE_COUNT}). The server derives the occurrence count from this — the client
   * never sends one, because the count depends on the series timezone and the first start.
   */
  untilDateUtc: string | null;
  /**
   * Weekly only: the weekdays the series runs on, as `Date.getDay()` numbers (0 = Sunday). Must
   * include the event's own first weekday. Omitted or empty means that weekday alone, which is
   * what a weekly repeat did before weekdays existed.
   *
   * An API older than this field ignores it and makes a single-day series, so the API has to
   * deploy before a client that lets a coach pick more than one day.
   */
  daysOfWeek?: number[];
};

/**
 * Which occurrences of a repeating series a write applies to.
 *
 * "This" is the default and is also what a non-recurring event has always done, so omitting it
 * keeps the pre-existing behavior exactly. "This" additionally DETACHES the occurrence, so a later
 * ThisAndFollowing / All write leaves the week the coach deliberately moved alone.
 */
export type RecurrenceEditScope = "This" | "ThisAndFollowing" | "All";

/** `GET /api/calendarevents/{id}/scope-count`: how many live events a scoped delete would remove. */
export interface RecurrenceScopeCountResponse {
  count: number;
}

/** Body for updating a calendar event. The create shape plus the recurrence scope. */
export type UpdateCalendarEventRequest = CreateCalendarEventRequest & {
  /** Omit for a non-recurring event; the server treats absent as "This". */
  scope?: RecurrenceEditScope;
};

/**
 * Lifecycle status of a scheduled call. `upcoming` calls are still to be made;
 * `complete` calls have happened (the server stamps `completedAt` on transition).
 */
export type CallStatus = "upcoming" | "complete";

/** A single org-scoped scheduled/recorded recruiting call. */
export type CallResponse = {
  id: string;
  prospectId: string;
  prospectName: string;
  /** The prospect's phone number for click-to-dial; null when none is on file. */
  prospectPhone: string | null;
  assignedCoachId: string;
  assignedCoachName: string;
  /**
   * ISO-8601 instant the call is scheduled for, or null when it is assigned but has no time
   * yet. A coach often divides a roster among their staff before anyone knows when each call
   * will happen.
   */
  scheduledAt: string | null;
  status: CallStatus;
  outcomeNotes: string | null;
  /**
   * Why the call was scheduled, carried from the batch that created it. Distinct from
   * `outcomeNotes`, which records how the call went and is mirrored onto the prospect
   * as a recruiting note.
   */
  purpose: string | null;
  /** Set by the server when `status` transitions to `complete`; null otherwise. */
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
};

/**
 * A scheduled call as it appears on the schedule (GET /api/calls/calendar).
 *
 * A slim projection of `CallResponse` — the calendar draws a chip per call across a whole
 * visible month, so it carries only what a chip renders plus the `id` it navigates to. Notes
 * in particular are absent.
 *
 * Only calls that HAVE a time are ever returned: a call with no `scheduledAt` has nothing to
 * place it on a calendar and stays on the call list until someone gives it one. That is why
 * `scheduledAt` is non-nullable here and nullable on `CallResponse`.
 */
export type CallCalendarItem = {
  id: string;
  prospectId: string;
  prospectName: string;
  assignedCoachId: string;
  assignedCoachName: string;
  /** ISO-8601 true UTC instant. Never null on this shape. */
  scheduledAt: string;
  status: CallStatus;
  /** Why the call exists, carried from the batch that created it. */
  purpose: string | null;
};

/** One prospect paired with the coach who will call them, and when. */
export type BatchCallAssignment = {
  prospectId: string;
  assignedCoachId: string;
  /**
   * ISO-8601 instant for this specific call. Wins over the batch-level `scheduledAt`,
   * which is only a fallback for assignments that omit their own.
   */
  scheduledAt?: string;
};

/** Body for creating a whole call list in one request. */
export type BatchCreateCallsRequest = {
  /**
   * Client-generated id for this batch. With it, a retried request replays instead of
   * creating a second set of calls, which matters because batch size is uncapped and
   * calls have no bulk delete.
   */
  runId?: string;
  /**
   * Fallback ISO-8601 instant for assignments that do not carry their own. Optional
   * because this client schedules every call individually.
   */
  scheduledAt?: string;
  /** Why the batch exists, applied to every call. Optional. */
  purpose?: string;
  assignments: BatchCallAssignment[];
};

export type CallBatchErrorCode =
  | "PROSPECT_NOT_FOUND"
  | "COACH_NOT_IN_ORGANIZATION"
  | "DUPLICATE_IN_BATCH_RUN"
  | "CREATE_FAILED"
  | "SCHEDULED_AT_MISSING";

/** Outcome for one assignment. Echoed in request order. */
export type BatchCallResult = {
  prospectId: string;
  success: boolean;
  /** The created call when `success`, otherwise null. */
  call: CallResponse | null;
  /** True when a retry replayed an existing call rather than creating a new one. */
  alreadyCreated: boolean;
  errorCode: CallBatchErrorCode | null;
  errorMessage: string | null;
};

/**
 * Per-assignment outcomes. Always HTTP 200: a partial run is a normal outcome the
 * client reports against the rows that failed.
 */
export type BatchCreateCallsResponse = {
  results: BatchCallResult[];
  succeededCount: number;
  failedCount: number;
};

/** Lean prospect row for pickers over a large filtered set. */
export type ProspectSummaryResponse = {
  id: string;
  firstName: string;
  lastName: string;
  position: string | null;
  gradYear: number | null;
  /** Year of junior college ("1".."3"). Optional - an older server omits it. */
  jucoYear?: JucoYear | null;
  /** Level ordinal: 0 High School, 1 JUCO, 2 Transfer Portal. `0` is falsy - test `!= null`. */
  playerLevel?: number | null;
  teams: TeamInfo[];
  defaultTags: ProspectDefaultTag[];
};

/** An unpaginated filtered read plus the count it was checked against. */
export type FilteredProspectsResponse = {
  items: ProspectSummaryResponse[];
  /** True number of matches, before the server's ceiling is applied. */
  totalCount: number;
  /** True when `totalCount` exceeded the ceiling, so `items` is partial. */
  isTruncated: boolean;
  maxRecords: number;
};

/** Body for scheduling a new call. */
export type CreateCallRequest = {
  prospectId: string;
  assignedCoachId: string;
  /** ISO-8601 instant. */
  scheduledAt: string;
  outcomeNotes?: string;
};

/**
 * Partial update for a call. Every field is optional — the single PATCH endpoint
 * powers reschedule (`scheduledAt`), reassign (`assignedCoachId`),
 * mark-complete/reopen (`status`), and note edits (`outcomeNotes`).
 */
export type UpdateCallRequest = {
  assignedCoachId?: string;
  scheduledAt?: string;
  status?: CallStatus;
  outcomeNotes?: string | null;
};
